import { Sparkles, AlertTriangle, ShieldCheck, Copy, RefreshCw, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { useToast } from '@/components/ui/ToastProvider'
import {
  useAiAcceptSuggestion,
  useAiAnalyzeInvoice,
  useAiInvoiceSuggestions,
  useAiRejectSuggestion,
  useAiStatus,
} from '@/lib/api/hooks'
import { hu } from '@/lib/i18n/hu'
import type {
  AiAnomalyPayload,
  AiApprovalPayload,
  AiCategoryPayload,
  AiSuggestion,
  Role,
} from '@/lib/api/types'

/**
 * AI javaslatok panelje az InvoiceDetailPage-en. 4 fajta:
 *  - category (kategória + ÁFA javaslat) — Accept = ráírja a számlára
 *  - anomaly (figyelmeztetés) — Accept = "tudomásul vettem", Reject = "nem releváns"
 *  - duplicate (másolat-gyanú) — link a gyanús számlákra
 *  - approval (jóváhagyási szint) — kontextuális javaslat a jóváhagyó kiválasztásához
 */
export function AiSuggestionsPanel({ invoiceId }: { invoiceId: string }) {
  const { data: aiState } = useAiStatus()
  const { data, isLoading } = useAiInvoiceSuggestions(invoiceId)
  const analyze = useAiAnalyzeInvoice()
  const accept = useAiAcceptSuggestion()
  const reject = useAiRejectSuggestion()
  const toast = useToast()

  if (!aiState?.enabled) {
    return (
      <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 text-xs text-slate-600 dark:text-slate-300 flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-slate-400" />
        <span>AI javaslatok kikapcsolva (nincs OpenAI API kulcs a backend env-ben).</span>
      </div>
    )
  }

  const suggestions = data?.suggestions ?? []
  const pending = suggestions.filter((s) => s.status === 'pending')

  async function runAnalyze() {
    try {
      await analyze.mutateAsync(invoiceId)
      toast.show({ tone: 'success', title: 'AI elemzés kész', description: 'A javaslatok itt jelennek meg.' })
    } catch (err) {
      toast.show({
        tone: 'error',
        title: 'AI elemzés sikertelen',
        description: err instanceof Error ? err.message : 'Ismeretlen hiba',
      })
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
          <Sparkles className="h-4 w-4 text-brand-600 dark:text-brand-300" />
          AI javaslatok
          {pending.length > 0 && (
            <span className="rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-200 text-[10px] px-1.5 py-0">
              {pending.length} új
            </span>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={runAnalyze} loading={analyze.isPending} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>
          Újraelemzés
        </Button>
      </div>

      {isLoading ? (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Spinner size={12} /> Töltés…
        </div>
      ) : suggestions.length === 0 ? (
        <div className="text-xs text-slate-500 dark:text-slate-400">
          Még nincs AI elemzés ezen a számlán. Kattints az <strong>Újraelemzés</strong> gombra.
        </div>
      ) : (
        <ul className="space-y-2">
          {suggestions.map((s) => (
            <SuggestionCard
              key={s.id}
              s={s}
              onAccept={() => accept.mutateAsync(s.id)}
              onReject={() => reject.mutateAsync(s.id)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function SuggestionCard({
  s,
  onAccept,
  onReject,
}: {
  s: AiSuggestion
  onAccept: () => Promise<unknown>
  onReject: () => Promise<unknown>
}) {
  const accepted = s.status === 'accepted'
  return (
    <li
      className={`rounded-md border px-3 py-2 ${
        accepted
          ? 'border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30'
          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/30'
      }`}
    >
      {s.kind === 'category' && <CategorySuggestion s={s} onAccept={onAccept} onReject={onReject} accepted={accepted} />}
      {s.kind === 'anomaly' && <AnomalySuggestion s={s} onAccept={onAccept} onReject={onReject} accepted={accepted} />}
      {s.kind === 'duplicate' && <DuplicateSuggestion s={s} onAccept={onAccept} onReject={onReject} accepted={accepted} />}
      {s.kind === 'approval' && <ApprovalSuggestion s={s} onAccept={onAccept} onReject={onReject} accepted={accepted} />}
    </li>
  )
}

function CategorySuggestion({
  s,
  onAccept,
  onReject,
  accepted,
}: {
  s: AiSuggestion
  onAccept: () => Promise<unknown>
  onReject: () => Promise<unknown>
  accepted: boolean
}) {
  const p = s.payload as unknown as AiCategoryPayload
  return (
    <div>
      <div className="flex items-start gap-2">
        <div className="text-xs uppercase tracking-wide font-semibold text-brand-700 dark:text-brand-300 mt-0.5">
          Kategória
        </div>
        <div className="flex-1">
          <div className="text-sm">
            <span className="font-semibold">{p.category}</span>{' '}
            <span className="text-slate-500 dark:text-slate-400">· ÁFA: {p.vatCategory}</span>{' '}
            <span className="text-[11px] text-slate-400">({p.confidence}%)</span>
          </div>
          {p.categoryReason && (
            <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{p.categoryReason}</div>
          )}
          {p.vatReason && (
            <div className="text-xs text-slate-500 dark:text-slate-400 italic mt-0.5">ÁFA indok: {p.vatReason}</div>
          )}
        </div>
        {!accepted && (
          <div className="flex gap-1 flex-shrink-0">
            <Button size="sm" variant="ghost" onClick={() => void onReject()} leftIcon={<X className="h-3 w-3" />}>
              Elvet
            </Button>
            <Button size="sm" onClick={() => void onAccept()} leftIcon={<Check className="h-3 w-3" />}>
              Alkalmaz
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function AnomalySuggestion({
  s,
  onAccept,
  onReject,
  accepted,
}: {
  s: AiSuggestion
  onAccept: () => Promise<unknown>
  onReject: () => Promise<unknown>
  accepted: boolean
}) {
  const p = s.payload as unknown as AiAnomalyPayload
  return (
    <div>
      <div className="flex items-start gap-2">
        <div className="text-xs uppercase tracking-wide font-semibold text-amber-700 dark:text-amber-300 mt-0.5">
          <AlertTriangle className="inline h-3 w-3 mr-0.5" /> Anomália
        </div>
        <div className="flex-1 space-y-1">
          {p.anomalies.map((a, i) => (
            <div key={i} className="text-sm">
              <span
                className={`inline-block rounded px-1.5 py-0 text-[10px] font-medium mr-2 ${
                  a.severity === 'high'
                    ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200'
                    : a.severity === 'warn'
                      ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200'
                      : 'bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-200'
                }`}
              >
                {a.severity}
              </span>
              <span className="text-slate-700 dark:text-slate-200">{a.explanation}</span>
              <span className="text-[10px] text-slate-400 ml-1">({a.kind})</span>
            </div>
          ))}
        </div>
        {!accepted && (
          <div className="flex gap-1 flex-shrink-0">
            <Button size="sm" variant="ghost" onClick={() => void onReject()}>
              Nem releváns
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void onAccept()}>
              Megnéztem
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function DuplicateSuggestion({
  s,
  onAccept,
  onReject,
  accepted,
}: {
  s: AiSuggestion
  onAccept: () => Promise<unknown>
  onReject: () => Promise<unknown>
  accepted: boolean
}) {
  const p = s.payload as unknown as AiAnomalyPayload
  return (
    <div>
      <div className="flex items-start gap-2">
        <div className="text-xs uppercase tracking-wide font-semibold text-red-700 dark:text-red-300 mt-0.5">
          <Copy className="inline h-3 w-3 mr-0.5" /> Duplikáció gyanú
        </div>
        <div className="flex-1 space-y-1">
          {p.duplicateCandidates.map((d) => (
            <div key={d.invoiceId} className="text-sm">
              <a
                href={`/invoices/${d.invoiceId}`}
                target="_blank"
                rel="noreferrer"
                className="underline text-brand-700 dark:text-brand-300"
              >
                {d.invoiceId.slice(0, 16)}…
              </a>{' '}
              <span className="text-[11px] text-slate-400">({d.confidence}%)</span>
              <div className="text-xs text-slate-600 dark:text-slate-300">{d.reason}</div>
            </div>
          ))}
        </div>
        {!accepted && (
          <div className="flex gap-1 flex-shrink-0">
            <Button size="sm" variant="ghost" onClick={() => void onReject()}>
              Külön
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void onAccept()}>
              Tudomásul véve
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function ApprovalSuggestion({
  s,
  onAccept,
  onReject,
  accepted,
}: {
  s: AiSuggestion
  onAccept: () => Promise<unknown>
  onReject: () => Promise<unknown>
  accepted: boolean
}) {
  const p = s.payload as unknown as AiApprovalPayload
  return (
    <div>
      <div className="flex items-start gap-2">
        <div className="text-xs uppercase tracking-wide font-semibold text-indigo-700 dark:text-indigo-300 mt-0.5">
          <ShieldCheck className="inline h-3 w-3 mr-0.5" /> Jóváhagyó
        </div>
        <div className="flex-1">
          <div className="text-sm">
            <span className="font-semibold">{hu.roles[p.suggestedApproverRole as Role] ?? p.suggestedApproverRole}</span>{' '}
            <span className="text-[11px] text-slate-400">({p.confidence}%)</span>
          </div>
          <div className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{p.reason}</div>
          {p.alsoNotify.length > 0 && (
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              CC: {p.alsoNotify.map((r) => hu.roles[r as Role] ?? r).join(', ')}
            </div>
          )}
        </div>
        {!accepted && (
          <div className="flex gap-1 flex-shrink-0">
            <Button size="sm" variant="ghost" onClick={() => void onReject()}>
              Más
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void onAccept()}>
              Tudomásul véve
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
